import { useEffect, useRef } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  /** Pergunta curta. Vira o nome acessivel do dialogo. */
  title: string;
  /** O que a acao faz de irreversivel. Omita quando nao houver nada a dizer. */
  message?: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Elementos que recebem foco dentro do dialogo, na ordem do DOM. */
const FOCAVEIS = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Confirmacao para exclusao de lista e de tag.
 *
 * **E uma excecao deliberada a convencao 6** ("desfazer, nao confirmar" —
 * spec 10.3), e nao um esquecimento. A convencao vale enquanto o desfazer
 * consegue devolver o estado anterior; apagar uma tag e o caso em que ele nao
 * consegue: o `CASCADE` leva as linhas de `task_tags` junto, e o desfazer
 * recria a TAG, nunca as marcacoes. Uma tag em trinta tarefas some com as
 * trinta marcacoes, e nenhum toast de 8 segundos traz isso de volta.
 *
 * O toast de desfazer CONTINUA depois de confirmar. Os dois cobrem coisas
 * diferentes: o dialogo evita o clique acidental, o toast devolve o que der
 * para devolver. Trocar um pelo outro deixaria um buraco em qualquer direcao.
 *
 * Foco: abre no "Cancelar", nunca no botao destrutivo — um Enter reflexo
 * confirmaria justamente o que o dialogo existe para segurar. Ao fechar, o
 * foco volta para quem abriu, senao ele cai no `<body>` e quem navega por
 * teclado perde o lugar na lista.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const painel = useRef<HTMLDivElement>(null);
  const cancelar = useRef<HTMLButtonElement>(null);
  const anterior = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;

    anterior.current = document.activeElement;
    cancelar.current?.focus();

    return () => {
      // `instanceof HTMLElement` porque `activeElement` pode ser um SVG ou ter
      // saido do documento enquanto o dialogo estava aberto.
      if (anterior.current instanceof HTMLElement && anterior.current.isConnected) {
        anterior.current.focus();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function aoTeclar(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      // Prende o Tab dentro do dialogo. Sem isso o foco escapa para a pagina
      // atras, que continua clicavel para o teclado enquanto o mouse ja esta
      // bloqueado pelo fundo — dois comportamentos para a mesma tela.
      if (event.key !== 'Tab' || !painel.current) return;

      const alvos = [...painel.current.querySelectorAll<HTMLElement>(FOCAVEIS)];
      const primeiro = alvos[0];
      const ultimo = alvos[alvos.length - 1];
      if (!primeiro || !ultimo) return;

      if (event.shiftKey && document.activeElement === primeiro) {
        event.preventDefault();
        ultimo.focus();
      } else if (!event.shiftKey && document.activeElement === ultimo) {
        event.preventDefault();
        primeiro.focus();
      }
    }

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/*
        O fundo cancela ao clique, como o Esc. E `aria-hidden` porque o leitor
        de tela ja tem o Cancelar; anunciar um retangulo clicavel sem nome so
        acrescentaria ruido.
      */}
      <div
        aria-hidden="true"
        onClick={onCancel}
        className="absolute inset-0 bg-black/40"
      />

      <div
        ref={painel}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        aria-describedby={message ? 'confirm-message' : undefined}
        className="relative w-full max-w-sm rounded bg-surface p-5 shadow-card"
      >
        <h2 className="font-display text-md font-semibold">{title}</h2>

        {message ? (
          <p id="confirm-message" className="mt-2 text-sm text-text-mut">
            {message}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelar}
            type="button"
            onClick={onCancel}
            className="min-h-touch rounded-sm px-4 text-sm font-medium hover:bg-surface-2"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={onConfirm}
            className="min-h-touch rounded bg-overdue px-4 text-sm font-medium text-white transition-opacity duration-150 hover:opacity-90"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
